/*
  FD brand: the "Complete the look" tiles, and the Soft sit foam upgrade.

  A tile is a toggle, not a second buy button: nothing is bought until the sofa
  is. When the basket button is pressed, the pressed tiles are added first and
  the sofa second, so Dawn's own add -- and the basket drawer it renders from
  the response -- reports the whole order in one go rather than a sofa followed
  by a drawer that is already out of date.

  The interception is a document-level CAPTURE listener on the basket button
  rather than a submit listener on the form. Both product-form.js and this file
  are deferred, so listener registration order on the form itself is not
  guaranteed; a capture listener on the document always runs first.

  Every piece is made in the sofa's own fabric, so the tile does not add "an
  armchair" -- it adds the armchair variant whose colour is the colour the sofa
  is set to. The map comes from the server; the fallback is the piece's own
  first available variant, never a guess.

  Since 2026-08-21 the SAME intercept also carries the paid comfort option.
  Soft sit is the supplier's medium foam and costs extra, so choosing it adds
  one unit of the `soft-sit-upgrade` variant the product's tier points at. It
  is one more entry in the same items array on purpose: a second listener on
  the same button would race this one, and the loser's add would be lost.

  Two consequences of adding the charge here rather than anywhere else:
  - The Soft sit radio is rendered `disabled` and enabled BELOW. If this file
    fails to load, the paid option simply cannot be chosen, so nobody gets
    medium foam without paying for it.
  - A failed add FAILS OPEN. The sofa is the order; it goes through without the
    upgrade and the failure is logged. fd-foam-guard.js then finds a sofa
    owing an upgrade line that is not there -- the same state as a shopper
    deleting it -- and turns the sofa line into the Firm option (user ruling
    2026-10-06), so nobody is ever left with Soft sit they were not charged for.
  - The upgrade is added BEFORE the sofa, in a separate request, so for a
    moment the basket holds an upgrade no sofa owes yet. `window.
    FdFoamAddPending` (a timestamp) covers that gap: fd-foam-guard.js waits
    while it is set rather than removing the "orphan", and it is cleared once
    the sofa add has settled, success or failure. The guard ignores a marker
    older than its own ttl, so a stuck one cannot switch the guard off.
*/
if (!customElements.get('fd-addons')) {
  customElements.define(
    'fd-addons',
    class FdAddons extends HTMLElement {
      connectedCallback() {
        this.tiles = Array.from(this.querySelectorAll('[data-fd-addon-tile]'));
        // Written by fd-comfort-picker.liquid, which is a different block, so
        // it is looked up on the document rather than inside this element.
        this.foam = this.readJson(document.querySelector('[data-fd-foam-config]'));
        if (!this.tiles.length && !this.foam) return;

        this.line = this.querySelector('[data-fd-addon-line]');
        this.lineText = this.querySelector('[data-fd-addon-line-text]');
        this.colourEcho = this.querySelector('[data-fd-addon-colour]');
        this.strings = this.readJson(this.querySelector('[data-fd-addon-strings]')) || {};
        this.productInfo = this.closest('product-info');
        this.form =
          this.productInfo?.querySelector('form[action*="/cart/add"]') ||
          document.querySelector('form[action*="/cart/add"]');

        this.colour = this.colourEcho ? this.colourEcho.textContent.trim() : '';

        this.tiles.forEach((tile) => {
          tile.variants = this.readJson(tile.querySelector('[data-fd-addon-variants]')) || [];
          // Absent when the piece has no media and the tile shipped the glyph
          // fallback instead; paint() skips the swap in that case.
          tile.img = tile.querySelector('[data-fd-addon-img]');
          tile.addEventListener('click', () => {
            tile.setAttribute('aria-pressed', tile.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
            this.paint();
          });
        });

        this.onOptionChange = (event) => this.trackColour(event);
        (this.productInfo || document).addEventListener('change', this.onOptionChange);

        this.onSubmitClick = (event) => this.interceptSubmit(event);
        document.addEventListener('click', this.onSubmitClick, true);

        if (this.foam) this.wireFoam();
        this.paint();
      }

      /*
        The paid comfort option, switched on. The radio ships disabled so a
        visitor without JavaScript can never choose an upgrade nothing would
        charge for; enabling it HERE, in the file that does the charging, keeps
        those two facts inseparable.
      */
      wireFoam() {
        this.foamRadio = document.getElementById(this.foam.radio_id);
        // `_foam_variant` plus the Firm-label stamps fd-foam-guard.js downgrades to.
        this.foamLines = Array.from(document.querySelectorAll('[data-fd-foam-line]'));
        if (!this.foamRadio) {
          this.foam = null;
          return;
        }
        this.foamRadio.disabled = false;

        // The radio is bound to the form by its `form` attribute, not by
        // nesting, so a change event never reaches the form. The fieldset it
        // does sit in holds both radios, so it hears every switch either way.
        this.foamFieldset = this.foamRadio.closest('.fd-comfort');
        this.onFoamChange = () => this.paintFoam();
        (this.foamFieldset || document).addEventListener('change', this.onFoamChange);
        this.paintFoam();
      }

      /*
        The hidden `_foam_variant` property rides on the SOFA line and is what
        fd-foam-guard.js counts. Disabled inputs are not submitted, so toggling
        `disabled` is the whole mechanism -- and it is kept in step on every
        change, not just at submit time, because a basket add that skips the
        intercept (no tiles, no upgrade) must still be correct.
      */
      paintFoam() {
        const off = !this.foamRadio.checked;
        this.foamLines.forEach((input) => {
          input.disabled = off;
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onSubmitClick, true);
        (this.productInfo || document).removeEventListener('change', this.onOptionChange);
        if (this.onFoamChange) {
          (this.foamFieldset || document).removeEventListener('change', this.onFoamChange);
        }
      }

      readJson(node) {
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          return null;
        }
      }

      trackColour(event) {
        const target = event.target;
        if (!target || target.dataset.optionName !== this.dataset.colourOption) return;
        this.colour = target.value;
        if (this.colourEcho) this.colourEcho.textContent = this.colour;
        this.paint();
      }

      chosen() {
        return this.tiles.filter((tile) => tile.getAttribute('aria-pressed') === 'true');
      }

      // The one place the sofa's colour is matched to a piece's variant: the
      // basket line and the tile's photo have to name the same one, so both
      // read it from here.
      matchFor(tile) {
        const match = tile.variants.find((variant) => variant.option === this.colour && variant.available);
        if (match) return match;
        return tile.variants.find((variant) => variant.available) || tile.variants[0] || null;
      }

      variantFor(tile) {
        const variant = this.matchFor(tile);
        return variant ? variant.id : null;
      }

      paint() {
        const names = [];
        this.tiles.forEach((tile) => {
          // A variant with no image keeps the last photo rather than blanking:
          // an empty frame reads as broken, the previous colour's photo of the
          // right piece does not. (Every buyable variant has a bound white-bg
          // master, so this is belt-and-braces.)
          if (tile.img) {
            const variant = this.matchFor(tile);
            if (variant && variant.image && tile.img.getAttribute('src') !== variant.image) {
              tile.img.setAttribute('src', variant.image);
            }
          }

          const on = tile.getAttribute('aria-pressed') === 'true';
          const label = on ? tile.dataset.removeLabel : tile.dataset.addLabel;
          if (label) tile.setAttribute('aria-label', label);
          if (on) names.push(tile.dataset.name);
        });

        if (!this.line) return;
        this.line.hidden = names.length === 0;
        if (names.length && this.lineText) {
          this.lineText.textContent = (this.strings.summary || '[items]')
            .replace('[items]', this.joinNames(names))
            .replace('[colour]', this.colour);
        }
      }

      // "a", "a and b", "a, b and c" -- the echo line is a sentence, not a list.
      joinNames(names) {
        if (names.length < 2) return names[0] || '';
        const join = this.strings.join || ' and ';
        return names.slice(0, -1).join(', ') + join + names[names.length - 1];
      }

      interceptSubmit(event) {
        if (this.pending || !this.form) return;
        const button = event.target.closest?.('[type="submit"]');
        if (!button || button.form !== this.form) return;

        const items = this.chosen()
          .map((tile) => this.variantFor(tile))
          .filter(Boolean)
          .map((id) => ({ id: id, quantity: 1 }));

        // The paid comfort option is bought exactly the way a tile is: one more
        // entry in the same array, posted by the same single intercept.
        // `_foam_upgrade` marks the line so fd-foam-guard.js can still find a
        // stale one after the sofa that paid for it has been removed.
        if (this.foam && this.foamRadio && this.foamRadio.checked) {
          items.push({
            id: this.foam.variant_id,
            quantity: 1,
            properties: { _foam_upgrade: '1' },
          });
        }

        if (!items.length) return;

        const foamAdd = items.some((item) => item.properties && item.properties._foam_upgrade);
        if (foamAdd) this.markFoamPending();

        event.preventDefault();
        event.stopPropagation();
        this.pending = true;

        // The `pending` flag alone only stops THIS listener from running twice.
        // It does not stop a second click from reaching product-form.js, whose
        // own double-submit guard has not engaged yet -- it engages on a real
        // submit, which is exactly what has just been deferred. A second click
        // in the fetch window would therefore add the sofa on its own, render
        // the basket drawer from that incomplete add, and leave the synthetic
        // click below to no-op against the guard it had just set. Locking the
        // real button up front closes that window, using the same attribute,
        // class and spinner product-form.js uses, so it reads as one busy
        // state rather than two competing ones.
        this.lock(button, true);

        fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items }),
        })
          .catch((error) => {
            // FAIL OPEN, deliberately, and consistently with the shipping
            // rules: the sofa is the order and must never be blocked by a line
            // beside it. A missed upgrade is not a lost charge either:
            // fd-foam-guard.js turns the sofa into the Firm option once the
            // sofa add settles, so the basket never claims an unpaid Soft sit.
            console.error('fd-addons: extra basket lines were not added', error);
          })
          .finally(() => {
            // Clear the tiles before handing back: they are in the basket now,
            // and a tile still showing "added" would add a second one.
            this.tiles.forEach((tile) => tile.setAttribute('aria-pressed', 'false'));
            this.paint();
            // Released on every path, success or failure: product-form.js skips
            // a submit whose button is still marked busy, so leaving the lock on
            // would silently swallow the sofa.
            this.lock(button, false);
            button.click();
            this.pending = false;
          });
      }

      /*
        Tells fd-foam-guard.js that an upgrade line is on its way in ahead of
        its sofa. Cleared by the sofa add's own outcome -- product-form.js
        publishes cartUpdate on success and cartError on failure -- or, if
        neither ever comes, by the timer. The guard has its own ttl as well.
      */
      markFoamPending() {
        window.FdFoamAddPending = Date.now();
        const clear = () => {
          window.FdFoamAddPending = 0;
          window.clearTimeout(timer);
          offs.forEach((off) => off && off());
        };
        const timer = window.setTimeout(clear, 10000);
        const offs = [];
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          offs.push(subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event && event.source === 'product-form') clear();
          }));
          offs.push(subscribe(PUB_SUB_EVENTS.cartError, clear));
        }
      }

      lock(button, on) {
        if (on) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
        button.classList.toggle('loading', on);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
        this.tiles.forEach((tile) => {
          tile.disabled = on;
        });
      }
    }
  );
}
